import { message } from "antd";
import { getAuth } from "firebase/auth";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import * as APITypes from "~/API";
import { apiClient, PaymentLoaderResult } from "~/services/apiClient";
import { INTENTS, ROUTES } from "~/utils";
import i18n from "~/i18n";

export const PaymentPageLoader = async ({ request }: LoaderFunctionArgs) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return redirect(ROUTES.home);

  const searchParams = new URL(request.url).searchParams;
  const reason = searchParams.get("reason") as APITypes.TransactionReason;
  const transaction_id = searchParams.get("transaction_id") ?? "";

  const response = await apiClient.loader<PaymentLoaderResult>("payment", {
    reason: reason ?? undefined,
    transaction_id: transaction_id || undefined,
  });

  if (!response.success || !response.data) {
    if (response.error?.includes("No active lease")) {
      return redirect(ROUTES.home);
    }
    return {
      lease: {} as APITypes.Lease,
      serviceRequests: [],
      transactions: [],
      reason,
      transaction_id,
    };
  }

  return {
    lease: response.data.lease,
    offer: response.data.offer,
    serviceRequests: response.data.serviceRequests ?? [],
    transactions: response.data.transactions ?? [],
    history: response.data.history,
    reason: response.data.reason ?? reason,
    transaction_id: response.data.transaction_id ?? transaction_id,
  };
};

export type PaymentPageLoaderData = {
  lease: APITypes.Lease;
  offer?: APITypes.Offer;
  serviceRequests?: APITypes.ServiceRequest[];
  transactions?: APITypes.Transaction[];
  history?: {
    month: number;
    year: number;
    paid: number;
    complete: boolean;
    expectedAmount?: number;
    periodStart?: string;
    periodEnd?: string;
  }[];
  reason?: APITypes.TransactionReason;
  rentRequired?: string[];
  transaction_id: string;
};

export const PaymentPageAction = async ({ request }: LoaderFunctionArgs) => {
  const auth = getAuth();
  if (!auth.currentUser) return redirect(ROUTES.home);

  const formData = await request.formData();
  const body = formData.get("body") as string;
  const intent = formData.get("intent");

  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(body ?? "{}") as Record<string, unknown>;
  } catch {
    message.error("Invalid request");
    return { error: "Invalid request" };
  }

  switch (intent) {
    case INTENTS._make_payment: {
      const result = await apiClient.sendIntent<APITypes.Transaction>(
        INTENTS._make_payment,
        payload,
      );
      if (result.success && result.data) {
        message.success(i18n.t("Payment_Successful"));
        return redirect(
          `${ROUTES.paymentsPage}?transaction_id=${result.data.id}`,
        );
      }
      message.error(result.error ?? "Payment failed");
      return { error: result.error ?? "Payment failed" };
    }
    case INTENTS._complete_payment: {
      const result = await apiClient.sendIntent<APITypes.Transaction>(
        INTENTS._complete_payment,
        payload,
      );
      if (result.success && result.data) {
        message.success(i18n.t("Payment_Successful"));
        return redirect(
          `${ROUTES.paymentsPage}?transaction_id=${result.data.id}`,
        );
      }
      message.error(result.error ?? "Payment failed");
      return { error: result.error ?? "Payment failed" };
    }
    default:
      return { error: "Invalid intent" };
  }
};
